/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getStructDecoder,
  getStructEncoder,
  getU16Decoder,
  getU16Encoder,
  getU32Decoder,
  getU32Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from "@solana/kit";
import {
  getAccessTierDecoder,
  getAccessTierEncoder,
  type AccessTier,
  type AccessTierArgs,
} from "../types";

export const STAKING_ACCOUNT_DISCRIMINATOR = new Uint8Array([
  52, 178, 251, 157, 180, 186, 98, 234,
]);

export function getStakingAccountDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    STAKING_ACCOUNT_DISCRIMINATOR,
  );
}

export type StakingAccount = {
  discriminator: ReadonlyUint8Array;
  /** Owner of this staking account (can register multiple agents) */
  owner: Address;
  /** Amount of GHOST tokens staked */
  amountStaked: bigint;
  /** Timestamp when staking started */
  stakedAt: bigint;
  /** Lock duration in seconds (minimum 30 days) */
  lockDuration: bigint;
  /** Timestamp when unlock is available */
  unlockAt: bigint;
  /** Reputation boost percentage (in basis points, e.g., 500 = 5%) */
  reputationBoostBps: number;
  /** Whether agent has "Verified" badge */
  hasVerifiedBadge: boolean;
  /** Whether agent has premium listing benefits */
  hasPremiumBenefits: boolean;
  /** Total slashed amount (never recoverable) */
  totalSlashed: bigint;
  /** Current access tier based on stake amount */
  tier: AccessTier;
  /** Daily API calls remaining (resets every 24h) */
  apiCallsRemaining: number;
  /** Last API quota reset timestamp */
  lastQuotaReset: bigint;
  /** Voting power for governance (equals amount_staked) */
  votingPower: bigint;
  /** Bump for PDA */
  bump: number;
};

export type StakingAccountArgs = {
  /** Owner of this staking account (can register multiple agents) */
  owner: Address;
  /** Amount of GHOST tokens staked */
  amountStaked: number | bigint;
  /** Timestamp when staking started */
  stakedAt: number | bigint;
  /** Lock duration in seconds (minimum 30 days) */
  lockDuration: number | bigint;
  /** Timestamp when unlock is available */
  unlockAt: number | bigint;
  /** Reputation boost percentage (in basis points, e.g., 500 = 5%) */
  reputationBoostBps: number;
  /** Whether agent has "Verified" badge */
  hasVerifiedBadge: boolean;
  /** Whether agent has premium listing benefits */
  hasPremiumBenefits: boolean;
  /** Total slashed amount (never recoverable) */
  totalSlashed: number | bigint;
  /** Current access tier based on stake amount */
  tier: AccessTierArgs;
  /** Daily API calls remaining (resets every 24h) */
  apiCallsRemaining: number;
  /** Last API quota reset timestamp */
  lastQuotaReset: number | bigint;
  /** Voting power for governance (equals amount_staked) */
  votingPower: number | bigint;
  /** Bump for PDA */
  bump: number;
};

/** Gets the encoder for {@link StakingAccountArgs} account data. */
export function getStakingAccountEncoder(): FixedSizeEncoder<StakingAccountArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["owner", getAddressEncoder()],
      ["amountStaked", getU64Encoder()],
      ["stakedAt", getI64Encoder()],
      ["lockDuration", getI64Encoder()],
      ["unlockAt", getI64Encoder()],
      ["reputationBoostBps", getU16Encoder()],
      ["hasVerifiedBadge", getBooleanEncoder()],
      ["hasPremiumBenefits", getBooleanEncoder()],
      ["totalSlashed", getU64Encoder()],
      ["tier", getAccessTierEncoder()],
      ["apiCallsRemaining", getU32Encoder()],
      ["lastQuotaReset", getI64Encoder()],
      ["votingPower", getU64Encoder()],
      ["bump", getU8Encoder()],
    ]),
    (value) => ({ ...value, discriminator: STAKING_ACCOUNT_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link StakingAccount} account data. */
export function getStakingAccountDecoder(): FixedSizeDecoder<StakingAccount> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["owner", getAddressDecoder()],
    ["amountStaked", getU64Decoder()],
    ["stakedAt", getI64Decoder()],
    ["lockDuration", getI64Decoder()],
    ["unlockAt", getI64Decoder()],
    ["reputationBoostBps", getU16Decoder()],
    ["hasVerifiedBadge", getBooleanDecoder()],
    ["hasPremiumBenefits", getBooleanDecoder()],
    ["totalSlashed", getU64Decoder()],
    ["tier", getAccessTierDecoder()],
    ["apiCallsRemaining", getU32Decoder()],
    ["lastQuotaReset", getI64Decoder()],
    ["votingPower", getU64Decoder()],
    ["bump", getU8Decoder()],
  ]);
}

/** Gets the codec for {@link StakingAccount} account data. */
export function getStakingAccountCodec(): FixedSizeCodec<
  StakingAccountArgs,
  StakingAccount
> {
  return combineCodec(getStakingAccountEncoder(), getStakingAccountDecoder());
}

export function decodeStakingAccount<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<StakingAccount, TAddress>;
export function decodeStakingAccount<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<StakingAccount, TAddress>;
export function decodeStakingAccount<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<StakingAccount, TAddress> | MaybeAccount<StakingAccount, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getStakingAccountDecoder(),
  );
}

export async function fetchStakingAccount<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<StakingAccount, TAddress>> {
  const maybeAccount = await fetchMaybeStakingAccount(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeStakingAccount<
  TAddress extends string = string,
>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<StakingAccount, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeStakingAccount(maybeAccount);
}

export async function fetchAllStakingAccount(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<StakingAccount>[]> {
  const maybeAccounts = await fetchAllMaybeStakingAccount(
    rpc,
    addresses,
    config,
  );
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeStakingAccount(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<StakingAccount>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) =>
    decodeStakingAccount(maybeAccount),
  );
}

export function getStakingAccountSize(): number {
  return 106;
}
